import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  WF_MODULE_OPTIONS,
  WF_TRIGGER_TYPE_OPTIONS,
  WF_RECORD_TYPES_BY_MODULE,
  WF_TIME_PERIOD_REF_OPTIONS,
  WF_RETRY_LIMIT_REF_OPTIONS,
  WF_RETRY_INTERVAL_REF_OPTIONS,
  WF_ROUTE,
  buildSampleWorkflowRules,
  createEmptyWorkflowForm,
  formatWorkflowConditionsDisplay,
  formatWorkflowDateTime,
  formatWorkflowTriggerDisplay,
  getAllowedNextStatuses,
  getAvailableWorkflowConditionFields,
  getRetryLimitNumericValue,
  getRetryLimitRefOption,
  getRetryIntervalRefOption,
  getStatusesForRecordType,
  getTimePeriodRefOption,
  getTriggerConditions,
  getWorkflowModuleLabel,
  getRecordTypeLabel,
  getStatusLabel,
  labelFromOptions,
  labelRetryIntervalRef,
  labelRetryLimitRef,
  labelTimePeriodRef,
  normalizeWorkflowForm,
  nextWorkflowRuleId,
} from '../../../data/workflowConfig';
import {
  canAddWorkflowRule,
  canEditWorkflowRule,
  canSaveWorkflowRule,
  canViewWorkflowConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveWorkflowRule,
} from '../../../data/workflowRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyWorkflowForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeWorkflowForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  const label = (getLabel && getLabel(value)) || value;
  return [...options, { value, label: `${label} (Unavailable)` }];
};

const WorkflowAddRule = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewWorkflowConfiguration(auth);
  const canAdd = canAddWorkflowRule(auth);
  const canEdit = canEditWorkflowRule(auth);
  const canSave = canSaveWorkflowRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleWorkflowRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const recordTypeOptions = WF_RECORD_TYPES_BY_MODULE[formData.module] || [];
  const statusOptions = getStatusesForRecordType(formData.recordType);
  const nextStatusOptions = getAllowedNextStatuses(formData.recordType, formData.currentStatus);
  const isTimeBased = formData.triggerType === 'time_based';
  const triggerOptions =
    formData.triggerType && formData.module && formData.recordType
      ? getTriggerConditions(formData.module, formData.recordType, formData.triggerType)
      : [];
  const conditionFields = getAvailableWorkflowConditionFields();
  const retryLimitValue = getRetryLimitNumericValue(formData.retryLimitRef);
  const retryIntervalApplicable = retryLimitValue != null && retryLimitValue > 0;

  const timePeriodOptions = withCurrentOption(
    WF_TIME_PERIOD_REF_OPTIONS,
    formData.timePeriodRef,
    labelTimePeriodRef,
  );
  const retryLimitOptions = withCurrentOption(
    WF_RETRY_LIMIT_REF_OPTIONS,
    formData.retryLimitRef,
    labelRetryLimitRef,
  );
  const retryIntervalOptions = withCurrentOption(
    WF_RETRY_INTERVAL_REF_OPTIONS,
    formData.retryIntervalRef,
    labelRetryIntervalRef,
  );

  const clearFieldErrors = (field) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        form: undefined,
        activation: undefined,
        duplicate: undefined,
        overlap: undefined,
        conflict: undefined,
        loop: undefined,
        concurrent: undefined,
        permission: undefined,
        checksUnavailable: undefined,
      };
      return nextErr;
    });
  };

  const handleChange = (field, value) => {
    clearFieldErrors(field);
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'module') {
        next.recordType = '';
        next.currentStatus = '';
        next.nextStatus = '';
        next.triggerEvent = '';
        next.timeStartsFrom = '';
        next.triggerCondition = '';
        next.conditions = [];
      }
      if (field === 'recordType') {
        next.currentStatus = '';
        next.nextStatus = '';
        next.triggerEvent = '';
        next.timeStartsFrom = '';
        next.triggerCondition = '';
        next.conditions = [];
      }
      if (field === 'currentStatus') {
        next.nextStatus = '';
      }
      if (field === 'triggerType') {
        next.triggerEvent = '';
        next.timeStartsFrom = '';
        next.triggerCondition = '';
        next.timePeriodRef = '';
        next.movementTiming = '';
      }
      if (field === 'retryLimitRef') {
        const limit = getRetryLimitNumericValue(value);
        if (limit == null || limit <= 0) next.retryIntervalRef = '';
      }
      return next;
    });
  };

  const buildRecord = (nowIso, userLabel) => {
    const normalized = normalizeWorkflowForm(formData);
    const id = formData.id || nextWorkflowRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...normalized,
      id,
      ruleName: normalized.ruleName.trim(),
      description: normalized.description.trim(),
      changeReason: normalized.changeReason.trim(),
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: null,
      configurationHistory: formData.configurationHistory || [],
      testHistory: formData.testHistory || [],
      dateAdded: isNew ? nowIso : formData.dateAdded || formData.createdOn || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || formData.createdBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
      createdOn: isNew ? nowIso : formData.createdOn || formData.dateAdded || nowIso,
      createdBy: isNew ? userLabel : formData.createdBy || formData.addedBy || userLabel,
      modifiedOn: nowIso,
      modifiedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));

    if ((isCreate && !canAdd) || (isEdit && !canEdit) || !canSave) {
      setErrors({
        permission: 'You do not have permission to save this Rule.',
        activation: 'You do not have permission to save this Rule.',
      });
      return;
    }

    const record = buildRecord(nowIso, userLabel);
    let result;
    try {
      result = saveWorkflowRule(allRules, record, publishedBaseline || {}, {
        originalLastUpdated: resolvedSource.lastUpdated || resolvedSource.modifiedOn,
        canSave: true,
        checksAvailable: true,
      });
    } catch {
      setErrors({
        checksUnavailable: 'Required checks could not be completed. Try again.',
        activation: 'Required checks could not be completed. Try again.',
      });
      return;
    }

    if (!result.success) {
      const next = { ...result.errors };
      if (result.summary.length) next.activation = result.summary.join(' ');
      setErrors(next);
      return;
    }

    navigate(WF_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`workflow-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? 'No compatible choices available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const renderTextarea = (name, label, placeholder, required = false) => {
    if (isView) return <TextValue label={label} value={formData[name]} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <textarea
          id={`workflow-${name}`}
          placeholder={placeholder}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
          rows={3}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const refReadOnlyHint = (option) => {
    if (!option) return null;
    return `${option.displayValue} ${option.unit}`;
  };

  const hasValidationErrors = Boolean(
    errors.activation
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.loop
    || errors.concurrent
    || errors.permission
    || errors.checksUnavailable
    || errors.form,
  );

  if (!canView || (isCreate && !canAdd) || (isEdit && !canEdit)) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Workflow</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          You do not have permission to {isView ? 'view' : isCreate ? 'add' : 'edit'} this Rule.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    const retryLimitOpt = getRetryLimitRefOption(formData.retryLimitRef);
    const retryIntervalOpt = getRetryIntervalRefOption(formData.retryIntervalRef);
    const timePeriodOpt = getTimePeriodRefOption(formData.timePeriodRef);
    const cancelEvents = Array.isArray(formData.cancellationEvents)
      ? formData.cancellationEvents.filter(Boolean)
      : [];
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Workflow Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <div className="sm:col-span-2">
                <TextValue label="Internal Description" value={formData.description} />
              </div>
              <TextValue label="Module" value={getWorkflowModuleLabel(formData.module)} />
              <TextValue label="Record Type" value={getRecordTypeLabel(formData.module, formData.recordType)} />
              <TextValue label="Current Status" value={getStatusLabel(formData.recordType, formData.currentStatus)} />
              <TextValue label="Next Status" value={getStatusLabel(formData.recordType, formData.nextStatus)} />
              <TextValue
                label="Trigger Type"
                value={labelFromOptions(WF_TRIGGER_TYPE_OPTIONS, formData.triggerType)}
              />
              {formData.triggerType === 'time_based' ? (
                <>
                  <TextValue label="Time Starts From" value={formData.timeStartsFrom || formData.triggerCondition} />
                  <TextValue
                    label="Time Period"
                    value={
                      timePeriodOpt
                        ? `${timePeriodOpt.label} · ${timePeriodOpt.displayValue} ${timePeriodOpt.unit}`
                        : formData.timePeriodRef
                          ? `${formData.timePeriodRef} (Unavailable)`
                          : '—'
                    }
                  />
                </>
              ) : (
                <TextValue label="Trigger Event" value={formData.triggerEvent || formData.triggerCondition} />
              )}
              <div className="sm:col-span-2">
                <TextValue label="Trigger" value={formatWorkflowTriggerDisplay(formData)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Conditions" value={formatWorkflowConditionsDisplay(formData)} />
              </div>
              <TextValue label="Leaves Current Status" value="Required" />
              <TextValue
                label="Cancellation Events"
                value={cancelEvents.length ? cancelEvents.join(', ') : 'None selected'}
              />
              <TextValue
                label="Retry Limit"
                value={
                  retryLimitOpt
                    ? `${retryLimitOpt.label} · ${retryLimitOpt.displayValue} ${retryLimitOpt.unit}`
                    : formData.retryLimitRef
                      ? `${formData.retryLimitRef} (Unavailable)`
                      : '—'
                }
              />
              <TextValue
                label="Retry Interval"
                value={
                  retryLimitValue === 0
                    ? 'Not applicable'
                    : retryIntervalOpt
                      ? `${retryIntervalOpt.label} · ${retryIntervalOpt.displayValue} ${retryIntervalOpt.unit}`
                      : formData.retryIntervalRef
                        ? `${formData.retryIntervalRef} (Unavailable)`
                        : '—'
                }
              />
              <TextValue label="Date Added" value={formatWorkflowDateTime(formData.dateAdded || formData.createdOn)} />
              <TextValue label="Added By" value={formData.addedBy || formData.createdBy} />
              <TextValue
                label="Last Updated"
                value={formatWorkflowDateTime(formData.lastUpdated || formData.modifiedOn)}
              />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy || formData.modifiedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Workflow Rule' : 'Edit Workflow Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="workflow-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
        </div>
        {renderTextarea('description', 'Internal Description', 'Describe the intended status change', true)}
        {renderSelect('module', 'Module', WF_MODULE_OPTIONS, 'Select module')}
        {renderSelect(
          'recordType',
          'Record Type',
          recordTypeOptions,
          formData.module ? 'Select record type' : 'Select module first',
          !formData.module,
        )}
        {renderSelect(
          'currentStatus',
          'Current Status',
          statusOptions,
          formData.recordType ? 'Select current status' : 'Select record type first',
          !formData.recordType,
        )}
        {renderSelect(
          'nextStatus',
          'Next Status',
          nextStatusOptions,
          formData.currentStatus ? 'Select next status' : 'Select current status first',
          !formData.currentStatus,
        )}
        {renderSelect('triggerType', 'Trigger Type', WF_TRIGGER_TYPE_OPTIONS, 'Select trigger type')}
        {isTimeBased ? (
          <>
            {renderSelect(
              'timeStartsFrom',
              'Time Starts From',
              withCurrentOption(triggerOptions, formData.timeStartsFrom, (v) => v),
              formData.triggerType
                ? formData.module && formData.recordType
                  ? 'Select timestamp'
                  : 'Select module and record type first'
                : 'Select trigger type first',
              !formData.triggerType || !formData.module || !formData.recordType,
            )}
            {renderSelect(
              'timePeriodRef',
              'Time Period',
              timePeriodOptions,
              'Select time period reference',
            )}
            {formData.timePeriodRef && getTimePeriodRefOption(formData.timePeriodRef) ? (
              <AdminConfigLockedField
                label="Returned Time Period"
                value={refReadOnlyHint(getTimePeriodRefOption(formData.timePeriodRef))}
                isView={false}
              />
            ) : null}
          </>
        ) : (
          renderSelect(
            'triggerEvent',
            'Trigger Event',
            withCurrentOption(triggerOptions, formData.triggerEvent, (v) => v),
            formData.triggerType
              ? formData.module && formData.recordType
                ? 'Select trigger event'
                : 'Select module and record type first'
              : 'Select trigger type first',
            !formData.triggerType || !formData.module || !formData.recordType,
          )
        )}
        <AdminConfigLockedField label="Leaves Current Status" value="Required" isView={false} />
        <AdminConfigLockedField label="Cancellation Events" value="None selected" isView={false} />
        {renderSelect('retryLimitRef', 'Retry Limit', retryLimitOptions, 'Select retry limit reference')}
        {formData.retryLimitRef && getRetryLimitRefOption(formData.retryLimitRef) ? (
          <AdminConfigLockedField
            label="Returned Retry Limit"
            value={refReadOnlyHint(getRetryLimitRefOption(formData.retryLimitRef))}
            isView={false}
          />
        ) : null}
        {retryIntervalApplicable ? (
          <>
            {renderSelect(
              'retryIntervalRef',
              'Retry Interval',
              retryIntervalOptions,
              'Select retry interval reference',
            )}
            {formData.retryIntervalRef && getRetryIntervalRefOption(formData.retryIntervalRef) ? (
              <AdminConfigLockedField
                label="Returned Retry Interval"
                value={refReadOnlyHint(getRetryIntervalRefOption(formData.retryIntervalRef))}
                isView={false}
              />
            ) : null}
          </>
        ) : formData.retryLimitRef && retryLimitValue === 0 ? (
          <AdminConfigLockedField label="Retry Interval" value="Not applicable" isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>Optional Conditions</AdminDetailFieldLabel>
          <AdminDetailFieldValue className="py-2">
            {conditionFields.length ? 'No Conditions added' : 'No compatible Conditions available'}
          </AdminDetailFieldValue>
          {conditionFields.length ? (
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1.5 text-sm font-medium text-mediumgray cursor-not-allowed"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
          ) : null}
        </div>
        {renderTextarea('changeReason', 'Change Reason', 'Enter a change reason', true)}
        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.loop ? <AdminFormFieldError>{errors.loop}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
          </div>
        ) : null}
        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default WorkflowAddRule;
